import { stableStringify } from '@de/utils/data-sources/dataSourceCache';

/**
 * Content identity for a published scope. Unlike JSON, it keeps `undefined`
 * apart from `null`, `NaN` apart from `null`, and boxed or special objects
 * apart from plain ones, so two scopes that interpolate differently never
 * share a signature.
 */
export function scopeContentSignature(
  scope: Readonly<Record<string, unknown>>,
): string {
  return stableStringify(encodeScopeValue(scope));
}

function encodeScopeValue(
  value: unknown,
  stack = new WeakSet<object>(),
): unknown {
  if (value === undefined) {
    return { t: 'u' };
  }
  if (value === null) {
    return { t: 'n' };
  }
  switch (typeof value) {
    case 'string':
      return { t: 's', v: value };
    case 'boolean':
      return { t: 'b', v: value };
    case 'number':
      if (Number.isNaN(value)) {
        return { t: 'num', v: 'nan' };
      }
      if (value === Infinity) {
        return { t: 'num', v: 'inf' };
      }
      if (value === -Infinity) {
        return { t: 'num', v: '-inf' };
      }
      return { t: 'num', v: value };
    case 'bigint':
      return { t: 'bi', v: value.toString() };
    case 'symbol':
      return { t: 'sym', v: String(value) };
    case 'function':
      return { t: 'f', v: Function.prototype.toString.call(value) };
    case 'object':
      break;
    default:
      return { t: 's', v: String(value) };
  }
  const special = encodeSpecialObject(value);
  if (special !== undefined) {
    return special;
  }
  if (stack.has(value)) {
    return { t: 'c' };
  }
  stack.add(value);
  try {
    if (Array.isArray(value)) {
      return {
        t: 'a',
        v: value.map((item) => encodeScopeValue(item, stack)),
        ...observableValueText(value),
      };
    }
    const entries: Record<string, unknown> = {};
    for (const key of Object.keys(value)) {
      entries[key] = encodeScopeValue(
        (value as Record<string, unknown>)[key],
        stack,
      );
    }
    return { t: 'o', v: entries, ...observableValueText(value) };
  } finally {
    stack.delete(value);
  }
}

function observableValueText(value: object): { json: string; text: string } {
  let json = '';
  try {
    json = JSON.stringify(value) ?? '';
  } catch {
    json = '';
  }
  let text = '';
  try {
    text = String(value);
  } catch {
    text = '';
  }
  return { json, text };
}

function encodeSpecialObject(value: object): unknown | undefined {
  if (value instanceof Date) {
    const time = value.getTime();
    return { t: 'date', v: Number.isNaN(time) ? 'invalid' : time };
  }
  if (value instanceof RegExp) {
    return { t: 're', v: String(value) };
  }
  if (typeof URL !== 'undefined' && value instanceof URL) {
    return { t: 'url', v: value.href };
  }
  if (
    value instanceof Number ||
    value instanceof String ||
    value instanceof Boolean
  ) {
    return { t: 'box', v: encodeScopeValue(value.valueOf()) };
  }
  return undefined;
}
